import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Plus, FileText, FileXls, Trash, Package, Lock, ClockCounterClockwise,
  Clipboard, Users, ChartBar, CalendarBlank, ShoppingCart, Eye, Wrench, Notepad,
  CurrencyInr, Warning, ShieldCheck, UploadSimple, DownloadSimple, CheckCircle,
  XCircle, ArrowClockwise, Link, ShareNetwork, Copy, HardHat, Gear, Receipt, Clock, Flask,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ProjectFinancials } from './ProjectFinancials';
import { ResourceSummary } from './ResourceSummary';
import { ModuleGuide, WorkflowProgress, NextStepHint } from '../components/GuidanceComponents';
import { ObserverInsights } from '../components/ObserverInsights';
import { DemandDashboard, RecipeCoverageCard } from '../components/DemandComponents';
import { BOQExcelImport } from '../components/BOQExcelImport';
import { PlannedVsActual, HaltPanel } from '../components/SiteRegisterComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  'Confirmed': 'bg-green-100 text-green-800',
  'Interim Approval': 'bg-amber-100 text-amber-800',
  'On Hold': 'bg-orange-100 text-orange-800',
  'Client Not Reverting': 'bg-rose-100 text-rose-800',
  'Cancelled': 'bg-red-100 text-red-800',
  'Draft': 'bg-zinc-100 text-zinc-600',
};

const PO_STATUS_STYLES = {
  'Pending': { bg: 'bg-zinc-100 border-zinc-300', text: 'text-zinc-700', icon: XCircle },
  'Partial': { bg: 'bg-amber-100 border-amber-300', text: 'text-amber-700', icon: Warning },
  'Completed': { bg: 'bg-green-100 border-green-300', text: 'text-green-700', icon: CheckCircle },
  'Overridden': { bg: 'bg-violet-100 border-violet-300', text: 'text-violet-700', icon: ShieldCheck },
};

export const ProjectHub = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [quoteHistory, setQuoteHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeControl, setTimeControl] = useState(null);
  const [finAccess, setFinAccess] = useState({ is_financially_restricted: false });
  const [voSummary, setVoSummary] = useState(null);
  const [handoverCheck, setHandoverCheck] = useState(null);
  const [financialRisk, setFinancialRisk] = useState(null);
  const [handoverEnforcement, setHandoverEnforcement] = useState(null);
  const [executionGate, setExecutionGate] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [activeModule, setActiveModule] = useState(
    searchParams.get('tab') === 'control' ? 'control' :
    searchParams.get('tab') === 'financials' ? 'financials' :
    searchParams.get('tab') === 'compliance' ? 'compliance' :
    searchParams.get('tab') === 'vo' ? 'vo' : 'boq'
  );

  const isConfirmed = project?.status === 'Confirmed';

  const fetchData = useCallback(async () => {
    try {
      const [projectRes, quotesRes, historyRes, tcRes, finRes, voRes, handoverRes, riskRes, gateRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/projects/${projectId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/quotes/project/${projectId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/quote-history`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/time-control`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/financial-access/check/${projectId}`, { withCredentials: true }).catch(() => ({ data: { is_financially_restricted: false } })),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/vo-summary`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/handover-check`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/financial-risk`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/execution-gate`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setProject(projectRes.data);
      setQuotes(quotesRes.data);
      setQuoteHistory(historyRes.data);
      setTimeControl(tcRes.data);
      setFinAccess(finRes.data);
      setVoSummary(voRes.data);
      setHandoverCheck(handoverRes.data);
      setFinancialRisk(riskRes.data);
      setExecutionGate(gateRes.data);
    } catch (error) {
      toast.error('Failed to load project details');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/quotes/${quoteId}`, { withCredentials: true });
      toast.success('Quote deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete quote');
    }
  };

  const handleCreateRevision = async (quoteId) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/quotes/enhanced/${quoteId}/revise`, {}, { withCredentials: true });
      toast.success(`Revised BOQ created: ${res.data.quote_ref}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create revision');
    }
  };

  const handleSaveDates = async (startDate, endDate) => {
    try {
      await axios.put(`${BACKEND_URL}/api/projects/${projectId}/dates`, {
        project_start_date: startDate, project_end_date: endDate,
      }, { withCredentials: true });
      toast.success('Project dates saved');
      fetchData();
    } catch { toast.error('Failed to save dates'); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="animate-pulse text-zinc-600">Loading...</div></div>;
  }
  if (!project) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="text-zinc-600">Project not found</div></div>;
  }

  const activeQuotes = quoteHistory.filter((q) => q.is_active);
  const archivedQuotes = quoteHistory.filter((q) => !q.is_active);

  const poComp = project.client_po_compliance || {};
  const poStatus = poComp.compliance_status || 'Pending';
  const executionAllowed = poStatus === 'Completed' || poStatus === 'Overridden';
  const showExecutionBanner = isConfirmed && !executionAllowed;

  return (
    <div>
      {project.is_demo && ['admin', 'super_admin'].includes(user.role) && (
        <div className="bg-amber-500 text-white text-center py-1.5 text-xs font-bold uppercase tracking-wide flex items-center justify-center gap-2" data-testid="demo-mode-banner">
          <Flask size={14} weight="fill" /> Demo Mode — seeded sample project (not a real client)
        </div>
      )}
      {/* Project Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button onClick={() => navigate('/dashboard')} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 mb-3" data-testid="back-button">
            <ArrowLeft size={18} className="mr-1.5" /> Dashboard
          </Button>
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{project.pi_number}</h1>
              {project.project_code && <p className="text-sm font-mono text-blue-700 mt-0.5" data-testid="project-code">{project.project_code}</p>}
              <p className="text-base text-zinc-600 mt-0.5">{project.client_name}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider rounded-sm ${
                  STATUS_COLORS[project.status] || 'bg-zinc-100 text-zinc-600'
                }`} data-testid="project-status-badge">
                  {project.status === 'Confirmed' && <Lock size={12} className="mr-1" />}
                  {project.status}
                </span>
                {isConfirmed && (
                  <POComplianceBadge status={poStatus} />
                )}
              </div>
              {/* Project Team Display */}
              {project.team && (project.team.project_manager_name || project.team.project_lead_name || project.team.accounts_contact_name) && (
                <div className="flex items-center gap-3 mt-2 text-[11px] text-zinc-500" data-testid="project-team-display">
                  {project.team.project_manager_name && (
                    <span><b className="text-zinc-600">PM:</b> {project.team.project_manager_name}</span>
                  )}
                  {project.team.project_lead_name && (
                    <span><b className="text-zinc-600">Lead:</b> {project.team.project_lead_name}</span>
                  )}
                  {project.team.accounts_contact_name && (
                    <span><b className="text-zinc-600">Accounts:</b> {project.team.accounts_contact_name}</span>
                  )}
                </div>
              )}
            </div>
            <SnapshotButton projectId={projectId} snapshotToken={project.snapshot_token} onRefresh={fetchData} />
          </div>

          {/* Time Control Card */}
          <TimeControlCard
            timeControl={timeControl}
            project={project}
            onSaveDates={handleSaveDates}
          />

          {/* Execution Blocked Banner */}
          {showExecutionBanner && (
            <ExecutionBlockedBanner
              projectId={projectId}
              poComp={poComp}
              user={user}
              onRefresh={fetchData}
              setActiveModule={setActiveModule}
            />
          )}

          {/* Workflow Progress */}
          <WorkflowProgress
            status={project.status}
            executionAllowed={executionAllowed}
            className="mt-4"
          />

          {/* Next Step Hint */}
          {project.status === 'Draft' && (
            <NextStepHint text="Create a BOQ quotation and move status to Interim Approval for review." className="mt-3" />
          )}
          {project.status === 'Interim Approval' && (
            <NextStepHint text="Review the BOQ and confirm the project to lock the quote and enable execution." className="mt-3" />
          )}
          {project.status === 'Confirmed' && !executionAllowed && (
            <NextStepHint text="Upload Client PO to release execution — Work Orders and Purchase Orders are currently blocked." className="mt-3" />
          )}
          {project.status === 'Confirmed' && executionAllowed && (
            <NextStepHint text="Execution is active. Create Work Orders, Purchase Orders, and track progress from the Control Tower." className="mt-3" />
          )}

          {/* Quick Actions */}
          {isConfirmed && (
            <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="project-quick-actions">
              <span className="text-[10px] font-bold uppercase text-zinc-400 mr-1">Quick:</span>
              <Button onClick={() => navigate(`/rfq-generator?project=${projectId}`)} size="sm" variant="outline" className="rounded-none border-zinc-300 text-xs h-7" data-testid="quick-rfq">
                <ShoppingCart size={14} className="mr-1" /> Generate RFQ
              </Button>
              <Button onClick={() => navigate(`/running-bill?project=${projectId}`)} size="sm" variant="outline" className="rounded-none border-zinc-300 text-xs h-7" data-testid="quick-running-bill">
                <Receipt size={14} className="mr-1" /> Running Bill
              </Button>
              <Button onClick={() => navigate(`/projects/${projectId}/gantt-simulation`)} size="sm" variant="outline" className="rounded-none border-zinc-300 text-xs h-7" data-testid="quick-gantt-sim">
                <ChartBar size={14} className="mr-1" /> Gantt Simulation
              </Button>
            </div>
          )}

          {/* Observer Insights (Project-level) */}
          {isConfirmed && <div className="mt-4"><ObserverInsights projectId={projectId} compact /></div>}

          {/* Recipe Coverage (compact) */}
          {isConfirmed && <RecipeCoverageCard projectId={projectId} />}

          {/* In Discussion Banner */}
          {project?.status === 'In Discussion' && (
            <InDiscussionBanner projectId={projectId} />
          )}

          {/* Execution Gate Banner */}
          {isConfirmed && executionGate && executionGate.state !== 'ready' && (
            <div className={`mt-4 border p-4 ${executionGate.state === 'hard_block' ? 'border-red-400 bg-red-50' : 'border-amber-400 bg-amber-50'}`} data-testid="execution-gate-banner">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={18} className={executionGate.state === 'hard_block' ? 'text-red-600' : 'text-amber-600'} />
                <span className={`text-sm font-bold ${executionGate.state === 'hard_block' ? 'text-red-800' : 'text-amber-800'}`}>
                  {executionGate.state === 'hard_block' ? 'Execution Blocked' : 'Execution — Resolve Before Proceeding'}
                </span>
                <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded ${executionGate.state === 'hard_block' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'}`}>
                  {executionGate.state.replace('_', ' ')}
                </span>
              </div>
              <div className="space-y-1">
                {(executionGate.blocks || []).map((b, i) => (
                  <div key={`b${i}`} className="flex items-center gap-2 text-xs text-red-700">
                    <XCircle size={13} className="shrink-0" /><span>{b.message}</span>
                    {b.action && <button onClick={() => setActiveModule('demand')} className="ml-1 text-red-800 font-bold underline text-[10px]">{b.action}</button>}
                  </div>
                ))}
                {(executionGate.warnings || []).map((w, i) => (
                  <div key={`w${i}`} className="flex items-center gap-2 text-xs text-amber-700">
                    <Warning size={13} className="shrink-0" /><span>{w.message}</span>
                    {w.action && <button onClick={() => setActiveModule('demand')} className="ml-1 text-amber-800 font-bold underline text-[10px]">{w.action}</button>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Module Tabs */}
          <div className="flex gap-1 mt-6 border-b border-zinc-200 -mb-px">
            <button
              onClick={() => setActiveModule('boq')}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                activeModule === 'boq' ? 'border-blue-700 text-blue-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'
              }`}
              data-testid="tab-boq-module"
            >
              <Clipboard size={16} className="inline mr-1.5 -mt-0.5" />
              BOQ Module
            </button>
            <button
              onClick={() => isConfirmed ? setActiveModule('control') : toast.info('Available only for Confirmed projects')}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                activeModule === 'control' ? 'border-blue-700 text-blue-700' : isConfirmed ? 'border-transparent text-zinc-500 hover:text-zinc-700' : 'border-transparent text-zinc-300 cursor-not-allowed'
              }`}
              data-testid="tab-control-tower"
            >
              <ChartBar size={16} className="inline mr-1.5 -mt-0.5" />
              Control Tower
              {!isConfirmed && <Lock size={12} className="inline ml-1 -mt-0.5" />}
            </button>
            <button
              onClick={() => setActiveModule('compliance')}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                activeModule === 'compliance' ? 'border-blue-700 text-blue-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'
              }`}
              data-testid="tab-compliance"
            >
              <ShieldCheck size={16} className="inline mr-1.5 -mt-0.5" />
              Client PO
              {showExecutionBanner && <Warning size={12} weight="fill" className="inline ml-1 -mt-0.5 text-red-500" />}
            </button>
            <button
              onClick={() => setActiveModule('financials')}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                activeModule === 'financials' ? 'border-blue-700 text-blue-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'
              } ${finAccess.is_financially_restricted ? 'hidden' : ''}`}
              data-testid="tab-financials"
            >
              <CurrencyInr size={16} className="inline mr-1.5 -mt-0.5" />
              Financials
            </button>
            {isConfirmed && (
              <button
                onClick={() => setActiveModule('vo')}
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                  activeModule === 'vo' ? 'border-blue-700 text-blue-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'
                }`}
                data-testid="tab-vo"
              >
                <FileText size={16} className="inline mr-1.5 -mt-0.5" />
                Variation Orders
                {voSummary && (voSummary.counts?.Draft > 0 || voSummary.counts?.Submitted > 0 || voSummary.counts?.['Under Review'] > 0) && (
                  <span className="ml-1 bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {(voSummary.counts?.Draft || 0) + (voSummary.counts?.Submitted || 0) + (voSummary.counts?.['Under Review'] || 0)}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={() => setActiveModule('resources')}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                activeModule === 'resources' ? 'border-blue-700 text-blue-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'
              }`}
              data-testid="tab-resources"
            >
              <Package size={16} className="inline mr-1.5 -mt-0.5" />
              Resources
            </button>
            {isConfirmed && (
              <button
                onClick={() => setActiveModule('demand')}
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                  activeModule === 'demand' ? 'border-blue-700 text-blue-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'
                }`}
                data-testid="tab-demand"
              >
                <Wrench size={16} className="inline mr-1.5 -mt-0.5" />
                Demand
              </button>
            )}
            {isConfirmed && (
              <button
                onClick={() => setActiveModule('execution')}
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                  activeModule === 'execution' ? 'border-blue-700 text-blue-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'
                }`}
                data-testid="tab-execution"
              >
                <HardHat size={16} className="inline mr-1.5 -mt-0.5" />
                Execution
              </button>
            )}
            <button
              onClick={() => setActiveModule('settings')}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                activeModule === 'settings' ? 'border-blue-700 text-blue-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'
              }`}
              data-testid="tab-settings"
            >
              <Gear size={16} className="inline mr-1.5 -mt-0.5" />
              Settings
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Handover Warning */}
        {isConfirmed && handoverCheck && !handoverCheck.ready_for_handover && ['admin', 'super_admin', 'project_lead'].includes(user?.role) && (
          <HandoverWarning check={handoverCheck} />
        )}

        {/* Financial Risk Summary Card */}
        {isConfirmed && financialRisk && financialRisk.total_risk > 0 && !finAccess.is_financially_restricted && (
          <FinancialRiskCard risk={financialRisk} />
        )}

        {/* Handover Enforcement Modal */}
        {handoverEnforcement && (
          <HandoverEnforcementModal
            blocks={handoverEnforcement.blocks}
            reason={overrideReason}
            setReason={setOverrideReason}
            userRole={user?.role}
            onOverride={async () => {
              if (!overrideReason.trim() || overrideReason.length < 5) { toast.error('Reason required (min 5 chars)'); return; }
              try {
                for (const block of handoverEnforcement.blocks) {
                  await axios.post(`${BACKEND_URL}/api/enforcement/override`, {
                    rule_type: block.rule,
                    entity_type: 'project',
                    entity_id: projectId,
                    project_id: projectId,
                    reason: overrideReason,
                    financial_impact: block.data?.amount || 0,
                  }, { withCredentials: true });
                }
                await handoverEnforcement.onProceed();
                setHandoverEnforcement(null);
                setOverrideReason('');
              } catch (e) { toast.error(e.response?.data?.detail || 'Override failed'); }
            }}
            onCancel={() => { setHandoverEnforcement(null); setOverrideReason(''); }}
          />
        )}

        {/* VO Summary Strip (shown in BOQ and Control views for confirmed projects) */}
        {isConfirmed && voSummary && voSummary.total_vos > 0 && activeModule !== 'vo' && !finAccess.is_financially_restricted && (
          <VOSummaryStrip summary={voSummary} onNavigate={() => setActiveModule('vo')} />
        )}

        {activeModule === 'boq' ? (
          <BOQModuleView
            project={project} activeQuotes={activeQuotes} archivedQuotes={archivedQuotes}
            user={user} navigate={navigate} projectId={projectId}
            onDelete={handleDeleteQuote} onRevise={handleCreateRevision}
            canSeeFinancials={!finAccess.is_financially_restricted}
          />
        ) : activeModule === 'control' ? (
          <ControlTowerView project={project} projectId={projectId} navigate={navigate} executionAllowed={executionAllowed} />
        ) : activeModule === 'vo' ? (
          <VOModuleRedirect projectId={projectId} navigate={navigate} />
        ) : activeModule === 'resources' ? (
          <ResourceSummary />
        ) : activeModule === 'demand' ? (
          <DemandDashboard projectId={projectId} />
        ) : activeModule === 'execution' ? (
          <div className="space-y-8">
            <PlannedVsActual projectId={projectId} />
            <HaltPanel projectId={projectId} />
          </div>
        ) : activeModule === 'settings' ? (
          <ProjectSettingsView projectId={projectId} project={project} user={user} />
        ) : activeModule === 'compliance' ? (
          <ClientPOComplianceView projectId={projectId} user={user} onRefresh={fetchData} project={project} />
        ) : (
          <ProjectFinancials projectId={projectId} />
        )}
      </main>
    </div>
  );
};

/* ========== SNAPSHOT BUTTON ========== */
const SnapshotButton = ({ projectId, snapshotToken, onRefresh }) => {
  const [generating, setGenerating] = useState(false);
  const [showLink, setShowLink] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/projects/${projectId}/generate-snapshot`, {}, { withCredentials: true });
      toast.success('Client snapshot link generated');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate snapshot'); }
    finally { setGenerating(false); }
  };

  const snapshotUrl = snapshotToken ? `${window.location.origin}/snapshot/${snapshotToken}` : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(snapshotUrl);
    toast.success('Snapshot link copied to clipboard');
  };

  const handleRevoke = async () => {
    if (!window.confirm('Revoke this snapshot link? The shared link will stop working.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/projects/${projectId}/snapshot-token`, { withCredentials: true });
      toast.success('Snapshot link revoked');
      onRefresh();
    } catch { toast.error('Failed to revoke'); }
  };

  if (!snapshotToken) {
    return (
      <Button onClick={handleGenerate} disabled={generating} variant="outline"
        className="border-zinc-300 rounded-none text-xs hover:bg-zinc-100" data-testid="generate-snapshot-btn">
        <ShareNetwork size={14} className="mr-1.5" />
        {generating ? 'Generating...' : 'Generate Client Snapshot'}
      </Button>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex gap-1">
        <Button onClick={() => setShowLink(!showLink)} variant="outline"
          className="border-green-300 text-green-700 rounded-none text-xs hover:bg-green-50" data-testid="view-snapshot-link-btn">
          <Link size={14} className="mr-1" /> Client Snapshot
        </Button>
        <Button onClick={() => window.open(`/snapshot/${snapshotToken}`, '_blank')} variant="outline"
          className="border-zinc-300 rounded-none text-xs hover:bg-zinc-100" data-testid="preview-snapshot-btn">
          <Eye size={14} />
        </Button>
      </div>
      {showLink && (
        <div className="flex items-center gap-1.5 bg-zinc-50 border border-zinc-200 p-2 mt-1">
          <input readOnly value={snapshotUrl} className="text-[10px] font-mono text-zinc-600 bg-transparent flex-1 w-64 outline-none" data-testid="snapshot-url-input" />
          <button onClick={handleCopy} className="p-1 hover:bg-zinc-200 text-zinc-600" title="Copy link" data-testid="copy-snapshot-link">
            <Copy size={12} />
          </button>
          <button onClick={handleRevoke} className="p-1 hover:bg-red-100 text-red-500" title="Revoke link" data-testid="revoke-snapshot-link">
            <XCircle size={12} />
          </button>
        </div>
      )}
    </div>
  );
};


/* ========== PO COMPLIANCE BADGE ========== */
const POComplianceBadge = ({ status }) => {
  const style = PO_STATUS_STYLES[status] || PO_STATUS_STYLES['Pending'];
  const Icon = style.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold border rounded-sm ${style.bg} ${style.text}`} data-testid="po-compliance-badge">
      <Icon size={12} weight="fill" /> PO: {status}
    </span>
  );
};


/* ========== EXECUTION BLOCKED BANNER ========== */
const ExecutionBlockedBanner = ({ projectId, poComp, user, onRefresh, setActiveModule }) => {
  const missing = [];
  if (!poComp.client_po_number) missing.push('PO Number');
  if (!poComp.po_document_url) missing.push('PO Document');
  const isSuperAdmin = user?.role === 'super_admin';

  const handleDownloadNotice = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/execution-hold-notice/pdf`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Execution_Hold_Notice.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to generate notice'); }
  };

  return (
    <div className="mt-4 border-2 border-red-300 bg-red-50 p-4" data-testid="execution-blocked-banner">
      <div className="flex items-start gap-3">
        <Warning size={24} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-bold text-red-800">Execution Blocked — Client PO Pending</h3>
          <p className="text-xs text-red-700 mt-1">Work Orders, Purchase Orders, and financial commitments are blocked until Client PO compliance is fulfilled.</p>
          {missing.length > 0 && (
            <div className="mt-2">
              <span className="text-xs font-semibold text-red-700">Missing:</span>
              <ul className="list-disc list-inside text-xs text-red-600 mt-0.5">
                {missing.map(m => <li key={m}>{m}</li>)}
              </ul>
            </div>
          )}
          <div className="flex gap-2 mt-3 flex-wrap">
            <Button size="sm" className="bg-red-700 hover:bg-red-800 text-white rounded-none h-7 text-xs" onClick={() => setActiveModule('compliance')} data-testid="go-to-compliance-btn">
              <UploadSimple size={14} className="mr-1" /> Upload Client PO
            </Button>
            <Button size="sm" variant="outline" className="border-red-300 text-red-700 rounded-none h-7 text-xs hover:bg-red-100" onClick={handleDownloadNotice} data-testid="download-hold-notice-btn">
              <DownloadSimple size={14} className="mr-1" /> Download Hold Notice
            </Button>
            {isSuperAdmin && (
              <OverrideButton projectId={projectId} onRefresh={onRefresh} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


/* ========== OVERRIDE BUTTON (inline in banner) ========== */
const OverrideButton = ({ projectId, onRefresh }) => {
  const [showInput, setShowInput] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleOverride = async () => {
    if (!reason.trim()) { toast.error('Override reason is mandatory'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/projects/${projectId}/po-compliance/override`, { reason }, { withCredentials: true });
      toast.success('PO compliance overridden by Super Admin');
      setShowInput(false);
      setReason('');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Override failed'); }
    finally { setSubmitting(false); }
  };

  if (!showInput) {
    return (
      <Button size="sm" variant="outline" className="border-violet-300 text-violet-700 rounded-none h-7 text-xs hover:bg-violet-100" onClick={() => setShowInput(true)} data-testid="override-btn">
        <ShieldCheck size={14} className="mr-1" /> Super Admin Override
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2 w-full mt-1">
      <Input
        value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="Mandatory: Enter override reason..."
        className="rounded-none h-7 text-xs flex-1 border-violet-300"
        data-testid="override-reason-input"
      />
      <Button size="sm" disabled={submitting} className="bg-violet-700 hover:bg-violet-800 text-white rounded-none h-7 text-xs" onClick={handleOverride} data-testid="confirm-override-btn">
        Confirm
      </Button>
      <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs" onClick={() => setShowInput(false)}>Cancel</Button>
    </div>
  );
};


/* ========== CLIENT PO COMPLIANCE VIEW ========== */
const ClientPOComplianceView = ({ projectId, user, onRefresh, project }) => {
  const [compliance, setCompliance] = useState(null);
  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState('');
  const [poValue, setPoValue] = useState('');
  const [remarks, setRemarks] = useState('');
  const [poFile, setPoFile] = useState(null);

  const fetchCompliance = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/po-compliance`, { withCredentials: true });
      setCompliance(res.data.compliance);
      setRelease(res.data.execution_release);
      setPoNumber(res.data.compliance.client_po_number || '');
      setPoDate(res.data.compliance.client_po_date || '');
      setPoValue(res.data.compliance.client_po_value || '');
      setRemarks(res.data.compliance.remarks || '');
    } catch { toast.error('Failed to load compliance data'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchCompliance(); }, [fetchCompliance]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('client_po_number', poNumber);
      fd.append('client_po_date', poDate);
      fd.append('client_po_value', poValue || 0);
      fd.append('remarks', remarks);
      if (poFile) fd.append('po_document', poFile);
      const res = await axios.put(`${BACKEND_URL}/api/projects/${projectId}/po-compliance`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('PO compliance updated');
      setCompliance(res.data.compliance);
      setRelease(res.data.execution_release);
      setPoFile(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const handleDownloadNotice = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/execution-hold-notice/pdf`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Execution_Hold_Notice.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to generate notice'); }
  };

  const isSuperAdmin = user?.role === 'super_admin';
  const canEdit = ['super_admin', 'admin', 'project_team'].includes(user?.role);
  const compStatus = compliance?.compliance_status || 'Pending';
  const statusStyle = PO_STATUS_STYLES[compStatus] || PO_STATUS_STYLES['Pending'];
  const StatusIcon = statusStyle.icon;

  if (loading) return <div className="animate-pulse text-zinc-600 py-8">Loading compliance data...</div>;

  return (
    <div className="space-y-6">
      <ModuleGuide text="Upload the Client Purchase Order to release execution. Work Orders and Purchase Orders are blocked until the PO is verified or overridden by a Super Admin." />

      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-zinc-800">Client PO Compliance</h2>
          <p className="text-sm text-zinc-500 mt-0.5">Purchase Order verification gate for execution release</p>
        </div>
        <div className="flex gap-2">
          {compStatus !== 'Completed' && compStatus !== 'Overridden' && (
            <Button size="sm" variant="outline" className="border-red-300 text-red-700 rounded-none h-8 text-xs hover:bg-red-50" onClick={handleDownloadNotice} data-testid="download-hold-notice-tab-btn">
              <DownloadSimple size={14} className="mr-1" /> Execution Hold Notice
            </Button>
          )}
        </div>
      </div>

      {/* Status Card */}
      <div className={`border-2 p-5 ${statusStyle.bg}`} data-testid="compliance-status-card">
        <div className="flex items-center gap-3">
          <StatusIcon size={32} weight="fill" className={statusStyle.text} />
          <div>
            <p className={`text-lg font-bold ${statusStyle.text}`}>Compliance Status: {compStatus}</p>
            {release && !release.allowed && (
              <p className="text-xs text-red-600 mt-0.5">Execution actions are blocked. Missing: {release.missing?.join(', ')}</p>
            )}
            {release && release.allowed && (
              <p className="text-xs text-green-700 mt-0.5">Execution released. All actions are available.</p>
            )}
          </div>
        </div>
        {compliance?.override_reason && (
          <div className="mt-3 pt-3 border-t border-violet-200">
            <p className="text-xs text-violet-700">
              <ShieldCheck size={12} className="inline mr-1" weight="fill" />
              <strong>Override:</strong> {compliance.override_reason}
              <span className="text-violet-500 ml-2">by {compliance.override_by_name} on {compliance.override_at ? new Date(compliance.override_at).toLocaleDateString() : ''}</span>
            </p>
          </div>
        )}
      </div>

      {/* PO Details Form */}
      <div className="bg-white border border-zinc-200 p-6">
        <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-4">Client Purchase Order Details</h3>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Client PO Number</Label>
              <Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} disabled={!canEdit}
                className="rounded-none mt-1" placeholder="Enter client PO number" data-testid="po-number-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Client PO Date</Label>
              <Input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} disabled={!canEdit}
                className="rounded-none mt-1" data-testid="po-date-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Client PO Value</Label>
              <Input type="number" step="0.01" value={poValue} onChange={(e) => setPoValue(e.target.value)} disabled={!canEdit}
                className="rounded-none mt-1" placeholder="0.00" data-testid="po-value-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">PO Document Upload</Label>
              <div className="mt-1 flex items-center gap-2">
                {canEdit && (
                  <label className="flex items-center gap-1 cursor-pointer border border-zinc-300 bg-zinc-50 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-100 transition-colors">
                    <UploadSimple size={14} />
                    {poFile ? poFile.name : 'Choose File'}
                    <input type="file" className="hidden" onChange={(e) => setPoFile(e.target.files[0])} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" data-testid="po-document-upload" />
                  </label>
                )}
                {compliance?.po_document_url && (
                  <a href={`${BACKEND_URL}/api${compliance.po_document_url}`} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-blue-700 underline flex items-center gap-1" data-testid="view-po-document">
                    <Eye size={12} /> View Uploaded PO
                  </a>
                )}
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Remarks</Label>
            <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} disabled={!canEdit}
              className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" placeholder="Any notes about the client PO..." data-testid="po-remarks-input" />
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Button type="submit" disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="save-compliance-btn">
                {saving ? 'Saving...' : 'Save Compliance'}
              </Button>
            </div>
          )}
        </form>
      </div>

      {/* Super Admin Override Section */}
      {isSuperAdmin && compStatus !== 'Completed' && compStatus !== 'Overridden' && (
        <SuperAdminOverridePanel projectId={projectId} onRefresh={() => { fetchCompliance(); onRefresh(); }} />
      )}
    </div>
  );
};


/* ========== SUPER ADMIN OVERRIDE PANEL ========== */
const SuperAdminOverridePanel = ({ projectId, onRefresh }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleOverride = async () => {
    if (!reason.trim()) { toast.error('Override reason is mandatory'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/projects/${projectId}/po-compliance/override`, { reason }, { withCredentials: true });
      toast.success('PO compliance overridden by Super Admin');
      setReason('');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Override failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="border-2 border-violet-200 bg-violet-50 p-5" data-testid="override-panel">
      <div className="flex items-start gap-3">
        <ShieldCheck size={24} weight="fill" className="text-violet-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-bold text-violet-800">Super Admin Override</h3>
          <p className="text-xs text-violet-600 mt-1">Bypass Client PO compliance to allow execution. This action is logged and audited.</p>
          <div className="flex items-center gap-2 mt-3">
            <Input
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Mandatory: Enter reason for override..."
              className="rounded-none flex-1 border-violet-300 text-sm"
              data-testid="override-panel-reason-input"
            />
            <Button disabled={submitting} className="bg-violet-700 hover:bg-violet-800 text-white rounded-none" onClick={handleOverride} data-testid="override-panel-confirm-btn">
              {submitting ? 'Processing...' : 'Override & Release'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};


/* ========== BOQ MODULE VIEW ========== */
const BOQModuleView = ({ project, activeQuotes, archivedQuotes, user, navigate, projectId, onDelete, onRevise, canSeeFinancials }) => {
  const isConfirmed = project.status === 'Confirmed';
  const canEdit = ['admin', 'super_admin', 'estimator', 'project_team'].includes(user.role);
  const isSuperAdmin = user.role === 'super_admin' || user.role === 'admin';
  const canCreateNew = canEdit && !isConfirmed;
  const [showImport, setShowImport] = useState(false);
  const [importQuoteId, setImportQuoteId] = useState('');
  const [guardrails, setGuardrails] = useState(null);

  useEffect(() => {
    if (activeQuotes.length > 0) {
      axios.get(`${BACKEND_URL}/api/quotes/${activeQuotes[0].id}/guardrails`, { withCredentials: true })
        .then(res => setGuardrails(res.data)).catch(() => {});
    }
  }, [activeQuotes]);

  return (
    <div className="space-y-8">
      {/* Guardrail Warnings */}
      {guardrails?.warnings?.length > 0 && (
        <div className="space-y-1.5" data-testid="boq-guardrails">
          {guardrails.warnings.map((w, i) => (
            <div key={i} className={`flex items-center gap-2 text-xs px-3 py-2 border rounded ${w.type === 'unit_inconsistency' ? 'border-red-300 bg-red-50 text-red-700' : 'border-amber-300 bg-amber-50 text-amber-700'}`}>
              <Warning size={14} className="shrink-0" />
              <span>{w.message}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-zinc-800">Quotations</h2>
          <p className="text-sm text-zinc-600 mt-0.5">{activeQuotes.length} active, {archivedQuotes.length} archived</p>
        </div>
        <div className="flex gap-2">
          {canEdit && !isConfirmed && activeQuotes.length > 0 && (
            <Button onClick={() => { setImportQuoteId(activeQuotes[0].id); setShowImport(!showImport); }} variant="outline" className="rounded-none border-zinc-300 text-xs" data-testid="toggle-excel-import">
              <FileXls size={16} className="mr-1" /> {showImport ? 'Close Import' : 'Excel Import'}
            </Button>
          )}
          {canCreateNew && (
            <Button onClick={() => navigate(`/projects/${projectId}/quotes/new`)} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="create-quote-button">
              <Plus size={18} className="mr-1.5" /> New Quotation
            </Button>
          )}
        </div>
        {isConfirmed && (
          <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 font-medium">
            <Lock size={12} className="inline mr-1 -mt-0.5" /> BOQ Locked — View / Export / VO Only
          </span>
        )}
      </div>

      {/* Excel Import */}
      {showImport && importQuoteId && (
        <BOQExcelImport quoteId={importQuoteId} onComplete={() => { setShowImport(false); window.location.reload(); }} />
      )}

      {activeQuotes.length === 0 ? (
        <div className="border border-zinc-200 bg-white p-12 text-center">
          <FileText size={48} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-base font-medium text-zinc-600">No quotations yet</p>
          <p className="text-sm text-zinc-500 mt-1">Create your first quotation for this project</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activeQuotes.map((q) => (
            <QuoteCard key={q.id} q={q} projectId={projectId} navigate={navigate}
              user={user} onDelete={onDelete} onRevise={onRevise}
              isSuperAdmin={isSuperAdmin} isConfirmed={isConfirmed} canSeeFinancials={canSeeFinancials} />
          ))}
        </div>
      )}

      {archivedQuotes.length > 0 && (
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-1.5">
            <ClockCounterClockwise size={16} /> Revision History ({archivedQuotes.length})
          </h3>
          <div className="space-y-2">
            {archivedQuotes.map((q) => (
              <div key={q.id} className="bg-zinc-100 border border-zinc-200 p-4 flex items-center justify-between opacity-70" data-testid={`archived-quote-${q.id}`}>
                <div>
                  <span className="text-sm font-semibold text-zinc-700">{q.title}</span>
                  <span className="ml-2 text-xs font-mono text-zinc-500">{q.quote_ref || ''}</span>
                  <span className="ml-2 text-xs text-zinc-400">{q.line_items_count} items{canSeeFinancials ? ` · Total: ₹${q.total?.toLocaleString('en-IN')}` : ''}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-zinc-200 text-zinc-600 px-2 py-0.5 font-medium"><Lock size={10} className="inline mr-0.5" /> Archived</span>
                  <Button onClick={() => navigate(`/projects/${projectId}/quotes/${q.id}`)} variant="outline" size="sm" className="border-zinc-300 rounded-none h-7 text-xs" data-testid={`view-archived-${q.id}`}>
                    <Eye size={14} className="mr-1" /> View
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


/* ========== QUOTE CARD ========== */
const QuoteCard = ({ q, projectId, navigate, user, onDelete, onRevise, isSuperAdmin, isConfirmed, canSeeFinancials }) => (
  <div className="bg-white border border-zinc-200 p-5 hover:shadow-sm transition-shadow" data-testid={`quote-card-${q.id}`}>
    <div className="flex justify-between items-start flex-wrap gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-base font-semibold text-zinc-800">{q.title}</h3>
          {q.quote_ref && <span className="text-xs font-mono bg-blue-50 text-blue-700 px-1.5 py-0.5 border border-blue-200">{q.quote_ref}</span>}
          {q.is_locked && <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 border border-green-200"><Lock size={10} className="inline mr-0.5" /> Confirmed</span>}
          {!q.is_locked && <span className="text-xs bg-zinc-50 text-zinc-600 px-1.5 py-0.5 border border-zinc-200">{q.status || 'Draft'}</span>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-xs">
          {canSeeFinancials ? (
            <>
              <div><span className="text-zinc-500 font-bold uppercase tracking-wider">Total</span><p className="font-bold text-blue-700 mt-0.5 text-base">₹{q.total?.toLocaleString('en-IN')}</p></div>
              <div><span className="text-zinc-500 font-bold uppercase tracking-wider">Subtotal</span><p className="font-medium text-zinc-900 mt-0.5">₹{q.subtotal?.toLocaleString('en-IN')}</p></div>
            </>
          ) : (
            <>
              <div><span className="text-zinc-500 font-bold uppercase tracking-wider">Total</span><p className="font-mono text-zinc-400 mt-0.5">***</p></div>
              <div><span className="text-zinc-500 font-bold uppercase tracking-wider">Subtotal</span><p className="font-mono text-zinc-400 mt-0.5">***</p></div>
            </>
          )}
          <div><span className="text-zinc-500 font-bold uppercase tracking-wider">Items</span><p className="font-medium text-zinc-900 mt-0.5">{q.line_items_count}</p></div>
          {q.revision_number > 0 && <div><span className="text-zinc-500 font-bold uppercase tracking-wider">Revision</span><p className="font-medium text-zinc-900 mt-0.5">R{q.revision_number}</p></div>}
        </div>
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {q.is_locked && (
          <Button onClick={() => navigate(`/quotes/${q.id}/materials`)} variant="outline" size="sm" className="border-zinc-300 rounded-none h-8 text-xs" data-testid={`materials-${q.id}`}>
            <Package size={14} className="mr-1" /> Materials
          </Button>
        )}
        <Button onClick={() => navigate(`/projects/${projectId}/quotes/${q.id}`)} variant="outline" size="sm" className="border-zinc-300 rounded-none h-8 text-xs" data-testid={`view-quote-${q.id}`}>
          {q.is_locked ? <><Eye size={14} className="mr-1" /> View</> : 'Edit'}
        </Button>
        {q.is_locked && isSuperAdmin && (
          <Button onClick={() => onRevise(q.id)} variant="outline" size="sm" className="border-amber-300 text-amber-700 rounded-none h-8 text-xs hover:bg-amber-50" data-testid={`revise-${q.id}`}>
            <ClockCounterClockwise size={14} className="mr-1" /> Create Revised BOQ
          </Button>
        )}
        {!q.is_locked && ['admin', 'super_admin', 'estimator', 'project_team'].includes(user.role) && (
          <Button onClick={() => onDelete(q.id)} variant="outline" size="sm" className="border-red-300 text-red-700 rounded-none h-8 text-xs hover:bg-red-50" data-testid={`delete-quote-${q.id}`}>
            <Trash size={14} />
          </Button>
        )}
      </div>
    </div>
  </div>
);


/* ========== CONTROL TOWER VIEW ========== */
const TimeControlCard = ({ timeControl, project, onSaveDates }) => {
  const [editing, setEditing] = React.useState(false);
  const [startDate, setStartDate] = React.useState(project?.project_start_date || '');
  const [endDate, setEndDate] = React.useState(project?.project_end_date || '');

  React.useEffect(() => {
    setStartDate(project?.project_start_date || '');
    setEndDate(project?.project_end_date || '');
  }, [project]);

  const tc = timeControl;
  const hasDates = tc?.has_dates;

  const statusColors = {
    on_track: { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-700', bar: 'bg-green-500' },
    at_risk: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', bar: 'bg-amber-500' },
    delayed: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', bar: 'bg-red-500' },
    unknown: { bg: 'bg-zinc-50', border: 'border-zinc-300', text: 'text-zinc-500', bar: 'bg-zinc-400' },
  };
  const sc = statusColors[tc?.status] || statusColors.unknown;

  const handleSave = () => {
    if (startDate && endDate) {
      onSaveDates(startDate, endDate);
      setEditing(false);
    }
  };

  return (
    <div className={`mt-4 border ${hasDates ? sc.border : 'border-zinc-200'} ${hasDates ? sc.bg : 'bg-zinc-50'} p-3`} data-testid="time-control-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <CalendarBlank size={14} className={hasDates ? sc.text : 'text-zinc-400'} />
          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Project Timeline</span>
        </div>
        {hasDates && (
          <span className={`text-xs font-bold px-2 py-0.5 ${sc.bg} ${sc.text} border ${sc.border}`} data-testid="time-control-status">
            {tc.status_label}
          </span>
        )}
      </div>

      {!hasDates && !editing ? (
        <div className="flex items-center justify-between">
          <p className="text-xs text-zinc-500">Set project start and end dates to enable time tracking.</p>
          <button onClick={() => setEditing(true)} className="text-xs text-blue-700 font-semibold hover:underline" data-testid="set-dates-btn">Set Dates</button>
        </div>
      ) : editing || !hasDates ? (
        <div className="flex items-center gap-2 flex-wrap">
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="border border-zinc-300 px-2 py-1 text-xs" data-testid="project-start-date" />
          <span className="text-xs text-zinc-400">to</span>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="border border-zinc-300 px-2 py-1 text-xs" data-testid="project-end-date" />
          <button onClick={handleSave} className="bg-blue-700 text-white text-xs px-3 py-1 font-semibold hover:bg-blue-800" data-testid="save-dates-btn">Save</button>
          {hasDates && <button onClick={() => setEditing(false)} className="text-xs text-zinc-500 hover:underline">Cancel</button>}
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-4 text-xs mb-2">
            <span className="text-zinc-500">{tc.project_start_date} — {tc.project_end_date}</span>
            <span className="font-semibold text-zinc-700">{tc.days_remaining} days left</span>
            <button onClick={() => setEditing(true)} className="text-blue-700 font-semibold hover:underline text-[10px]" data-testid="edit-dates-btn">Edit</button>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex justify-between text-[10px] mb-1">
                <span className="text-zinc-500">Time: <strong>{tc.time_progress_pct}%</strong></span>
                <span className={`font-bold ${sc.text}`}>Work: <strong>{tc.work_progress_pct}%</strong></span>
              </div>
              <div className="relative h-3 bg-zinc-200 overflow-hidden" data-testid="time-control-bar">
                <div className="absolute h-full bg-zinc-400 opacity-40" style={{ width: `${tc.time_progress_pct}%` }} />
                <div className={`absolute h-full ${sc.bar}`} style={{ width: `${tc.work_progress_pct}%` }} />
              </div>
            </div>
            <span className={`text-sm font-bold ${sc.text} min-w-[60px] text-right`} data-testid="time-control-deviation">
              {tc.deviation > 0 ? '+' : ''}{tc.deviation}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const ControlTowerView = ({ project, projectId, navigate, executionAllowed }) => {
  const modules = [
    { key: 'material', label: 'Material Planning', desc: 'Track material orders, budgeted vs actual costs', icon: Package, path: `/projects/${projectId}/control/material`, color: 'blue', needsRelease: false },
    { key: 'manpower', label: 'Manpower Planning', desc: 'Worker allocation, skilled/unskilled tracking', icon: Users, path: `/projects/${projectId}/control/manpower`, color: 'green', needsRelease: false },
    { key: 'gantt', label: 'Gantt Chart', desc: 'Visual timeline of trade-wise execution', icon: CalendarBlank, path: `/projects/${projectId}/control/gantt`, color: 'purple', needsRelease: false },
    { key: 'purchase', label: 'Purchase Schedule', desc: 'Auto-generated procurement timeline from BOQ', icon: ShoppingCart, path: `/projects/${projectId}/control/purchase`, color: 'orange', needsRelease: false },
    { key: 'work-orders', label: 'Work Orders', desc: 'Generate and manage work orders for vendors', icon: Wrench, path: `/projects/${projectId}/control/work-orders`, color: 'teal', needsRelease: true },
    { key: 'purchase-orders', label: 'Purchase Orders', desc: 'Generate and manage POs for material suppliers', icon: Notepad, path: `/projects/${projectId}/control/purchase-orders`, color: 'indigo', needsRelease: true },
    { key: 'budget', label: 'Budget vs Actual', desc: 'Aggregate comparison with variance tracking', icon: ChartBar, path: `/projects/${projectId}/control/budget`, color: 'red', needsRelease: false },
    { key: 'execution', label: 'Execution Center', desc: 'Daily progress, manpower & site logs', icon: HardHat, path: `/projects/${projectId}/execution`, color: 'amber', needsRelease: false },
  ];

  const colorMap = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100',
    green: 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100',
    purple: 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100',
    orange: 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100',
    red: 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100',
    teal: 'bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100',
    amber: 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100',
  };

  return (
    <div>
      <h2 className="text-lg font-semibold tracking-tight text-zinc-800 mb-1">Project Control Tower</h2>
      <p className="text-sm text-zinc-500 mb-6">Execution management for {project.pi_number}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {modules.map((m) => {
          const Icon = m.icon;
          const blocked = m.needsRelease && !executionAllowed;
          return (
            <button
              key={m.key}
              onClick={() => blocked ? toast.error('Execution blocked — Client PO compliance pending. Upload PO or request override.') : navigate(m.path)}
              className={`border p-5 text-left transition-all ${blocked ? 'opacity-60 cursor-not-allowed border-zinc-200 bg-zinc-50 text-zinc-400' : colorMap[m.color]}`}
              data-testid={`control-${m.key}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <Icon size={28} />
                {blocked && <Lock size={14} className="text-red-500" />}
              </div>
              <h3 className="text-base font-semibold">{m.label}</h3>
              <p className="text-xs mt-1 opacity-80">{m.desc}</p>
              {blocked && <p className="text-xs mt-2 text-red-500 font-medium">Client PO Required</p>}
            </button>
          );
        })}
      </div>
    </div>
  );
};


/* ========== VO MODULE REDIRECT ========== */
const VOModuleRedirect = ({ projectId, navigate }) => {
  React.useEffect(() => {
    navigate(`/projects/${projectId}/variation-orders`);
  }, [projectId, navigate]);
  return <div className="text-center py-8 text-zinc-500">Redirecting to Variation Orders...</div>;
};


/* ========== VO SUMMARY STRIP ========== */
const VOSummaryStrip = ({ summary, onNavigate }) => (
  <div className="mb-6 bg-white border border-zinc-200 p-3 flex items-center justify-between cursor-pointer hover:bg-zinc-50 transition-colors" onClick={onNavigate} data-testid="vo-summary-strip">
    <div className="flex items-center gap-4">
      <FileText size={18} className="text-blue-600" />
      <span className="text-sm font-semibold text-zinc-800">Variation Orders</span>
      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 border border-blue-200 font-bold">{summary.total_vos} VOs</span>
      {summary.counts?.Approved > 0 && <span className="text-xs text-green-700">Approved: {summary.counts.Approved}</span>}
      {(summary.counts?.Draft > 0 || summary.counts?.Submitted > 0 || summary.counts?.['Under Review'] > 0) && (
        <span className="text-xs text-orange-700 font-medium">
          <Warning size={12} className="inline mr-0.5 -mt-0.5" weight="fill" />
          Pending: {(summary.counts?.Draft || 0) + (summary.counts?.Submitted || 0) + (summary.counts?.['Under Review'] || 0)}
        </span>
      )}
    </div>
    <div className="flex items-center gap-3">
      {summary.approved_value != null && summary.approved_value > 0 && (
        <span className="text-sm font-bold text-green-700">+Rs.{summary.approved_value.toLocaleString('en-IN')}</span>
      )}
      {summary.at_risk_value != null && summary.at_risk_value > 0 && (
        <span className="text-xs text-orange-600 font-medium">At Risk: Rs.{summary.at_risk_value.toLocaleString('en-IN')}</span>
      )}
      <span className="text-xs text-blue-700 font-semibold">View All →</span>
    </div>
  </div>
);


/* ========== HANDOVER WARNING ========== */
const HandoverWarning = ({ check }) => {
  if (!check.warnings || check.warnings.length === 0) return null;
  return (
    <div className="mb-6 border-2 border-amber-300 bg-amber-50 p-4" data-testid="handover-warning">
      <div className="flex items-start gap-3">
        <Warning size={20} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-bold text-amber-800">Financial Exposure Before Handover</h3>
          <ul className="mt-2 space-y-1">
            {check.warnings.map((w, i) => (
              <li key={i} className={`text-xs flex items-center gap-1.5 ${w.severity === 'high' ? 'text-red-700' : 'text-amber-700'}`}>
                <Warning size={10} weight="fill" />
                {w.message}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};


const fmt = (v) => `Rs.${(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/* ========== FINANCIAL RISK SUMMARY CARD ========== */
const FinancialRiskCard = ({ risk }) => {
  const RISK_COLORS = { green: 'border-green-300 bg-green-50', amber: 'border-amber-300 bg-amber-50', red: 'border-red-300 bg-red-50' };
  const RISK_TEXT = { green: 'text-green-700', amber: 'text-amber-700', red: 'text-red-700' };
  const borderColor = RISK_COLORS[risk.risk_level] || RISK_COLORS.amber;
  const textColor = RISK_TEXT[risk.risk_level] || RISK_TEXT.amber;

  return (
    <div className={`mb-6 border-2 ${borderColor} p-4`} data-testid="financial-risk-card">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} weight="fill" className={textColor} />
          <h3 className="text-sm font-bold text-zinc-800">Financial Risk Summary</h3>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${borderColor} ${textColor}`}>
            {risk.risk_level === 'green' ? 'SAFE' : risk.risk_level === 'amber' ? 'MODERATE RISK' : 'HIGH RISK'}
          </span>
          {risk.override_count > 0 && (
            <span className="text-[10px] font-bold bg-red-100 text-red-700 border border-red-200 px-1.5 py-0.5" data-testid="override-badge">
              {risk.override_count} override(s)
            </span>
          )}
        </div>
        <span className={`text-lg font-bold ${textColor}`} data-testid="total-risk-value">{fmt(risk.total_risk)}</span>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3">
        <div className="text-center">
          <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">VO Not Billed</div>
          <div className={`text-base font-bold mt-0.5 ${risk.unbilled_vo_value > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmt(risk.unbilled_vo_value)}</div>
        </div>
        <div className="text-center">
          <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Bills Not Invoiced</div>
          <div className={`text-base font-bold mt-0.5 ${risk.uninvoiced_bill_value > 0 ? 'text-amber-600' : 'text-green-600'}`}>{fmt(risk.uninvoiced_bill_value)}</div>
        </div>
        <div className="text-center">
          <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Invoices Not Collected</div>
          <div className={`text-base font-bold mt-0.5 ${risk.uncollected_invoice_value > 0 ? 'text-orange-600' : 'text-green-600'}`}>{fmt(risk.uncollected_invoice_value)}</div>
        </div>
      </div>
      {risk.overdue_invoices?.length > 0 && (
        <div className="mt-2 pt-2 border-t border-zinc-200">
          <div className="text-[10px] font-bold text-red-600 uppercase tracking-wider">Overdue Invoices</div>
          {risk.overdue_invoices.map((inv, i) => (
            <div key={i} className="text-xs text-red-700 flex gap-2 mt-0.5">
              <span className="font-mono">{inv.invoice_number}</span>
              <span className="font-bold">{fmt(inv.invoice_value)}</span>
              <span className="text-red-500">{inv.days_overdue} days overdue</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ========== HANDOVER ENFORCEMENT MODAL ========== */
const HandoverEnforcementModal = ({ blocks, reason, setReason, userRole, onOverride, onCancel }) => {
  const canOverride = userRole === 'super_admin';

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="handover-enforcement-modal">
      <div className="bg-white w-full max-w-lg shadow-2xl border-2 border-red-400">
        <div className="bg-red-50 border-b-2 border-red-300 px-5 py-3.5 flex items-center gap-3">
          <Warning size={22} weight="fill" className="text-red-600" />
          <h2 className="text-sm font-bold text-red-900">Project Completion Blocked</h2>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-red-800 font-medium">Financial exposure detected. The project cannot be marked as Completed until these issues are resolved:</p>
          {blocks.map((block, i) => (
            <div key={i} className="border border-red-200 bg-red-50 p-3 text-sm flex items-start gap-2">
              <Warning size={14} weight="fill" className="text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-red-800 font-semibold">{block.message}</p>
                {block.data?.amount > 0 && <p className="text-xs text-red-600 font-bold mt-0.5">{fmt(block.data.amount)}</p>}
              </div>
            </div>
          ))}
          {canOverride ? (
            <div className="border-2 border-amber-300 bg-amber-50 p-3">
              <label className="text-xs font-bold text-amber-800 block mb-1">Super Admin Override — Mandatory Reason</label>
              <textarea value={reason} onChange={e => setReason(e.target.value)}
                className="w-full border border-amber-300 bg-white px-3 py-2 text-sm resize-none h-16"
                placeholder="Provide justification for closing the project with financial exposure..."
                data-testid="handover-override-reason" />
            </div>
          ) : (
            <div className="border border-zinc-200 bg-zinc-50 p-3 text-center">
              <p className="text-sm text-zinc-600">Only Super Admin can override this block.</p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200">
          <Button variant="outline" onClick={onCancel} className="rounded-none border-zinc-300 text-xs">Cancel</Button>
          {canOverride && (
            <Button onClick={onOverride} className="bg-red-700 text-white rounded-none text-xs" data-testid="handover-override-btn">Override & Complete</Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ========== IN DISCUSSION BANNER ==========
const InDiscussionBanner = ({ projectId }) => {
  const [checklist, setChecklist] = useState(null);
  const [showChecklist, setShowChecklist] = useState(false);

  const fetchChecklist = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/confirmation-checklist`, { withCredentials: true });
      setChecklist(res.data);
      setShowChecklist(true);
    } catch {}
  };

  return (
    <div className="mt-4 border-2 border-violet-400 bg-violet-50 p-4" data-testid="in-discussion-banner">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock size={18} className="text-violet-600" />
          <div>
            <span className="text-sm font-bold text-violet-800">Project In Discussion</span>
            <p className="text-[10px] text-violet-600 mt-0.5">Demand generation, indents, manpower, and execution modules are locked. Confirm the project to unlock.</p>
          </div>
        </div>
        <button onClick={fetchChecklist} className="bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold px-4 py-2 rounded-none" data-testid="show-checklist-btn">
          Confirmation Checklist
        </button>
      </div>
      {showChecklist && checklist && (
        <div className="mt-3 bg-white border border-violet-200 p-3 space-y-1.5" data-testid="confirmation-checklist">
          {checklist.items.map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              {item.passed ? <CheckCircle size={14} className="text-emerald-600 shrink-0" /> : <XCircle size={14} className="text-red-500 shrink-0" />}
              <span className={item.passed ? 'text-zinc-700' : 'text-red-700 font-medium'}>{item.label}</span>
              <span className="text-zinc-400 text-[10px]">{item.detail}</span>
            </div>
          ))}
          <p className="text-[10px] text-violet-600 mt-2 font-medium">
            {checklist.can_confirm ? 'Ready to confirm. Change project status to "Confirmed" in Settings.' : 'BOQ required before confirmation.'}
          </p>
        </div>
      )}
    </div>
  );
};


// ========== PROJECT SETTINGS VIEW (Trade Selection + Overhead + Payment Health) ==========
const ProjectSettingsView = ({ projectId, project, user }) => {
  const [trades, setTrades] = useState([]);
  const [selections, setSelections] = useState([]);
  const [overhead, setOverhead] = useState({ accommodation_pct: 0, travel_pct: 0, logistics_pct: 0, petty_cash_pct: 0, misc_pct: 0 });
  const [overheadCalc, setOverheadCalc] = useState(null);
  const [payHealth, setPayHealth] = useState(null);
  const [team, setTeam] = useState({ project_manager_id: '', project_lead_id: '', accounts_contact_id: '' });
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [tradesRes, selRes, overRes, payRes, teamRes, usersRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/trades`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/projects/${projectId}/trade-selection`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/projects/${projectId}/overhead-model`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/projects/${projectId}/payment-health`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/projects/${projectId}/team`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/users`, { withCredentials: true }),
        ]);
        setTrades(tradesRes.data);
        const saved = selRes.data.trade_selections || [];
        setSelections(saved.length > 0 ? saved : tradesRes.data.map(t => ({ trade_id: t.id, trade_name: t.name, mode: t.trade_type === 'in_house' ? 'in_house' : 'subcontract' })));
        const om = overRes.data.overhead_model || {};
        setOverhead({ accommodation_pct: om.accommodation_pct || 0, travel_pct: om.travel_pct || 0, logistics_pct: om.logistics_pct || 0, petty_cash_pct: om.petty_cash_pct || 0, misc_pct: om.misc_pct || 0 });
        setOverheadCalc(overRes.data);
        setPayHealth(payRes.data);
        const t = teamRes.data.team || {};
        setTeam({ project_manager_id: t.project_manager_id || '', project_lead_id: t.project_lead_id || '', accounts_contact_id: t.accounts_contact_id || '' });
        setUsersList(Array.isArray(usersRes.data) ? usersRes.data : usersRes.data.users || []);
      } catch {}
      finally { setLoading(false); }
    };
    load();
  }, [projectId, BACKEND_URL]);

  const saveTradeSelection = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/projects/${projectId}/trade-selection`, { trade_selections: selections }, { withCredentials: true });
      toast.success('Trade selections saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };
  const toggleTrade = (tradeId) => {
    setSelections(s => s.some(t => t.trade_id === tradeId) ? s.filter(t => t.trade_id !== tradeId) : [...s, { trade_id: tradeId, trade_name: trades.find(t => t.id === tradeId)?.name || '', mode: 'in_house' }]);
  };
  const setTradeMode = (tradeId, mode) => {
    setSelections(s => s.map(t => t.trade_id === tradeId ? { ...t, mode } : t));
  };
  const saveOverhead = async () => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/projects/${projectId}/overhead-model`, overhead, { withCredentials: true });
      toast.success('Overhead model saved');
      const fresh = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/overhead-model`, { withCredentials: true });
      setOverheadCalc(fresh.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const saveTeam = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/projects/${projectId}/team`, team, { withCredentials: true });
      toast.success('Team assignment saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  if (loading) return <div className="text-center py-8 text-zinc-500">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="project-settings-view">
      {/* Payment Health Warning */}
      {payHealth && payHealth.payout_exceeds_receipts && (
        <div className="bg-red-50 border-2 border-red-300 p-4 flex items-start gap-3" data-testid="payment-hold-warning">
          <Warning size={24} className="text-red-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-bold text-red-700">Payout exceeds receipts</p>
            <p className="text-xs text-red-600 mt-1">
              Receipts: Rs {(payHealth.total_receipts || 0).toLocaleString('en-IN')} | Payouts: Rs {(payHealth.total_payouts || 0).toLocaleString('en-IN')} | Net: Rs {(payHealth.net_position || 0).toLocaleString('en-IN')}
            </p>
            <p className="text-[10px] text-red-500 mt-1">Review procurement and expense payouts before processing further payments.</p>
          </div>
        </div>
      )}

      {/* Payment Health Summary */}
      {payHealth && (
        <div data-testid="payment-health-section">
          <h3 className="text-base font-bold text-zinc-800 mb-3">Payment Health</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="bg-white border border-zinc-200 px-3 py-2.5">
              <p className="text-[10px] font-bold uppercase text-zinc-500">Client Receipts</p>
              <p className="text-lg font-bold text-green-700">Rs {(payHealth.total_receipts || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-white border border-zinc-200 px-3 py-2.5">
              <p className="text-[10px] font-bold uppercase text-zinc-500">GRN Payouts</p>
              <p className="text-lg font-bold text-zinc-800">Rs {(payHealth.grn_payouts || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-white border border-zinc-200 px-3 py-2.5">
              <p className="text-[10px] font-bold uppercase text-zinc-500">Expenses</p>
              <p className="text-lg font-bold text-zinc-800">Rs {(payHealth.expense_payouts || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-white border border-zinc-200 px-3 py-2.5">
              <p className="text-[10px] font-bold uppercase text-zinc-500">Total Payouts</p>
              <p className="text-lg font-bold text-zinc-800">Rs {(payHealth.total_payouts || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className={`border px-3 py-2.5 ${payHealth.payout_exceeds_receipts ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              <p className="text-[10px] font-bold uppercase text-zinc-500">Net Position</p>
              <p className={`text-lg font-bold ${payHealth.payout_exceeds_receipts ? 'text-red-700' : 'text-green-700'}`}>Rs {(payHealth.net_position || 0).toLocaleString('en-IN')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Team Assignment */}
      <div data-testid="team-assignment-section">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-bold text-zinc-800">Project Team</h3>
          {isAdmin && <Button onClick={saveTeam} size="sm" className="bg-blue-700 hover:bg-blue-800 text-white rounded-none text-xs" data-testid="save-team">Save Team</Button>}
        </div>
        <div className="bg-white border border-zinc-200 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { key: 'project_manager_id', label: 'Project Manager', required: true },
              { key: 'project_lead_id', label: 'Project Lead', required: false },
              { key: 'accounts_contact_id', label: 'Accounts Contact', required: false },
            ].map(f => (
              <div key={f.key}>
                <Label className="text-[10px] font-bold uppercase text-zinc-500">
                  {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
                </Label>
                <select
                  value={team[f.key] || ''}
                  onChange={e => setTeam(t => ({ ...t, [f.key]: e.target.value }))}
                  disabled={!isAdmin}
                  className="w-full mt-1 bg-zinc-50 border border-zinc-200 rounded-none p-2 text-xs text-zinc-800 disabled:opacity-60"
                  data-testid={`team-${f.key}`}
                >
                  <option value="">{f.required ? 'Select...' : '— Not assigned —'}</option>
                  {usersList.filter(u => u.status !== 'inactive').map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trade Selection */}
      <div data-testid="trade-selection-section">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-bold text-zinc-800">Trade Selection</h3>
          {isAdmin && <Button onClick={saveTradeSelection} size="sm" className="bg-blue-700 hover:bg-blue-800 text-white rounded-none text-xs" data-testid="save-trade-selection">Save Selections</Button>}
        </div>
        <div className="bg-white border border-zinc-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] font-bold uppercase text-zinc-500">Selected</th>
                <th className="px-3 py-2 text-left text-[10px] font-bold uppercase text-zinc-500">Trade</th>
                <th className="px-3 py-2 text-left text-[10px] font-bold uppercase text-zinc-500">Mode</th>
              </tr>
            </thead>
            <tbody>
              {trades.map(t => {
                const selected = selections.some(s => s.trade_id === t.id);
                const sel = selections.find(s => s.trade_id === t.id);
                return (
                  <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selected} onChange={() => toggleTrade(t.id)} disabled={!isAdmin} data-testid={`trade-check-${t.id}`} />
                    </td>
                    <td className="px-3 py-2 font-medium text-zinc-800">{t.name}</td>
                    <td className="px-3 py-2">
                      {selected ? (
                        <select value={sel?.mode || 'in_house'} onChange={e => setTradeMode(t.id, e.target.value)} disabled={!isAdmin}
                          className="bg-zinc-50 border border-zinc-200 rounded-none p-1.5 text-xs" data-testid={`trade-mode-${t.id}`}>
                          <option value="in_house">In-house</option>
                          <option value="subcontract">Subcontract</option>
                        </select>
                      ) : <span className="text-xs text-zinc-400">-</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Overhead Model */}
      <div data-testid="overhead-model-section">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-bold text-zinc-800">Indirect Cost / Overhead Model</h3>
          {isAdmin && <Button onClick={saveOverhead} size="sm" className="bg-blue-700 hover:bg-blue-800 text-white rounded-none text-xs" data-testid="save-overhead">Save Overhead</Button>}
        </div>
        <div className="bg-white border border-zinc-200 p-4">
          <div className="grid grid-cols-5 gap-4 mb-4">
            {[{ key: 'accommodation_pct', label: 'Accommodation %' }, { key: 'travel_pct', label: 'Travel %' }, { key: 'logistics_pct', label: 'Logistics %' }, { key: 'petty_cash_pct', label: 'Petty Cash %' }, { key: 'misc_pct', label: 'Misc %' }].map(f => (
              <div key={f.key}>
                <Label className="text-[10px] font-bold uppercase text-zinc-500">{f.label}</Label>
                <Input type="number" min="0" max="100" step="0.1" value={overhead[f.key]} disabled={!isAdmin}
                  onChange={e => setOverhead(o => ({ ...o, [f.key]: parseFloat(e.target.value) || 0 }))}
                  className="mt-1 rounded-none text-sm" data-testid={`overhead-${f.key}`} />
              </div>
            ))}
          </div>
          {overheadCalc && overheadCalc.computed && (
            <div className="border-t border-zinc-200 pt-3">
              <div className="flex items-center gap-3 text-xs text-zinc-500 mb-2">
                <span>Project Value: <b className="text-zinc-800">Rs {(overheadCalc.project_value || 0).toLocaleString('en-IN')}</b></span>
                <span>Total Overhead: <b className="text-zinc-800">Rs {(overheadCalc.total_overhead || 0).toLocaleString('en-IN')}</b></span>
                <span>({overheadCalc.total_overhead_pct || 0}%)</span>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {Object.entries(overheadCalc.computed).map(([key, val]) => (
                  <div key={key} className="bg-zinc-50 border border-zinc-100 px-2 py-1.5">
                    <p className="text-[10px] text-zinc-500 capitalize">{key.replace('_', ' ')}</p>
                    <p className="text-xs font-bold text-zinc-800">Rs {(val.final_amount || 0).toLocaleString('en-IN')}</p>
                    <p className="text-[10px] text-zinc-400">{val.percentage}%</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

